import { Experience } from "@ninetailed/experience.js-react";
import { getLink } from "@utils/linkUtils";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import FocusLock from "react-focus-lock";

import HamburgerMenu from "@/components/Header/HamburgerMenu";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
// import Image from "next/image";
import Image from "@/components/ui/Image/Image";
import Modal from "@/components/ui/Modal/Modal";
import ModalContent from "@/components/ui/Modal/ModalContent";
import { ModalsContext } from "@/context/ModalsContext";
import { NavigationContext } from "@/context/NavigationContext";
import { TemplateContext } from "@/context/TemplateContext";
import { UTMParametersContext, getFallbackCampaignParameters } from "@/context/UTMParametersContext";
import { UserContext } from "@/context/UserContext";
import {
  browsePropertiesHeaderLinkClick,
  contactChannelTracking,
  ctaClickTracking,
  navigationClickTracking,
  pushImmersiveExperience,
  searchInteractionTracking,
} from "@/dataLayer/dataLayer";
import { DL_KEYS } from "@/dataLayer/keys";
import useMediaQuery from "@/hooks/useMediaQuery";
import useScroll from "@/hooks/useScroll";
import { parseCustomExperience } from "@/lib/experiences";
import { HeaderProps } from "@/types";

import SoftLogin from "../SoftLogin/SoftLogin";

const LocationMobile = dynamic(() => import("@/components/ui/Form/Filters/LocationMobile"), { ssr: false });
const ContactChannels = dynamic(() => import("@/components/ContactChannels/ContactChannels"), { ssr: false });

const whiteMenu = {
  container: "top-0",
  containerBg: "bg-white",
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
};

const transparentMenu = {
  container: "top-0",
  containerBg: "bg-transparent",
  darkLogo: "hidden",
  lightLogo: "block",
  iconStroke: "stroke-white",
  hamburgerVariant: "light",
};

const variants = {
  open: { x: 0, display: "block" },
  close: { x: "100vw", transitionEnd: { display: "none" } },
};
const transition = {
  duration: 0.5,
};

// If a Ninetailed variant is personalised, use it; otherwise use the baseline/variant fields passed in
const resolveActiveItem = (currentEntry: any, item: any) => {
  let activeItem = currentEntry?.fields || currentEntry || item;
  if (currentEntry?.nt_variants && currentEntry.nt_variants.length > 0 && currentEntry?.ninetailed?.isPersonalized) {
    activeItem = currentEntry.nt_variants[0];
  }
  return activeItem;
};

// Menu button and panel are defined at module level so their identity is stable across header renders.
// Declaring them inside the .map() made React unmount/remount every button and panel on each render (e.g. on scroll).
const MobileMenuButton = (props: any) => {
  const { baseItem: item, itemIndex, menuItemRef } = props;
  const t = useTranslations();
  const { setMobilePanelID } = useContext(NavigationContext);
  const activeItem = resolveActiveItem(props, item);

  return (
    item.title && (
      <div
        ref={(el) => {
          menuItemRef.current[itemIndex] = el;
        }}
      >
        <Button
          className="px-s w-full py-s no-underline flex justify-between items-center"
          variant={ButtonVariant.TERTIARY}
          size={ButtonSize.SMALL}
          theme={ButtonTheme.BLACK}
          type="button"
          onClick={() => {
            setMobilePanelID(item.id);
            activeItem.internalName === "Immersive Experience" &&
              pushImmersiveExperience(activeItem?.title || "", activeItem?.title, "HeaderMobile", window.location.href);
            navigationClickTracking(activeItem?.title || item.title);
          }}
        >
          <>
            {activeItem.title}
            <Icon className="stroke-black" name="arrow-right" title={t("label.common.arrow")} size="16" />
          </>
        </Button>
      </div>
    )
  );
};

const MobileMenuPanel = (props: any) => {
  const { baseItem: item, highlightFirstCluster } = props;
  const t = useTranslations();
  const locale = useLocale();
  const { mobilePanelID, setMobilePanelID, setMobileMenuOpen } = useContext(NavigationContext);
  const active = mobilePanelID && mobilePanelID === item.id;
  const activeItem = resolveActiveItem(props, item);

  return (
    <motion.div
      id={`mobile-panel-mobile-${item.id}`}
      initial={variants.close}
      transition={transition}
      variants={variants}
      animate={active ? "open" : "close"}
      className={`translate-x-[100%] fixed z-40 left-0 top-0 w-screen h-dvh bg-white `}
    >
      <FocusLock group="menu" disabled={!active} className="w-full h-full overflow-hidden">
        <div className="flex flex-col h-full">
          <header className="flex gap-s justify-between items-center p-m shrink-0">
            <button
              onClick={() => {
                setMobilePanelID("");
              }}
            >
              <Icon className="stroke-black" name="arrow-left" title={t("label.common.arrow")} size="24" />
            </button>
            <p className="uppercase text-m font-primary overflow-hidden whitespace-nowrap text-ellipsis">{activeItem.title}</p>
            <button
              onClick={() => {
                setMobilePanelID("");
                setMobileMenuOpen(false);
              }}
            >
              <Icon name="close" className="stroke-black" size="24" title={t("label.common.close")} />
            </button>
          </header>
          <section className="flex-1 overflow-auto">
            {activeItem?.columns?.map((column: any, index: any) => {
              return (
                <div key={`column-${index}-${column?.id}`} className="py-m px-m">
                  <p className="py-s mb-xs text-s uppercase text-gray-50 font-primary font-medium">{column?.title}</p>
                  <ul>
                    {column?.links?.map((link: any, linkIndex: any) => {
                      return (
                        <li key={`mobile-menu-link-${link?.id}-${linkIndex}`}>
                          <Button
                            className={`${highlightFirstCluster && linkIndex === 0 ? "text-[14px] py-s" : "text-[14px] py-xs"} w-full no-underline flex justify-between items-center font-secondary font-medium`}
                            variant={ButtonVariant.TERTIARY}
                            size={ButtonSize.SMALL}
                            theme={ButtonTheme.BLACK}
                            type="button"
                            href={getLink(link, locale)}
                            onClick={() =>
                              browsePropertiesHeaderLinkClick(
                                { link_category: column?.title, link_name: link?.label },
                                activeItem?.internalName || "",
                              )
                            }
                          >
                            {link?.label}
                          </Button>
                        </li>
                      );
                    })}
                    {column?.viewAll && (
                      <li>
                        <Button
                          className={`${highlightFirstCluster && index === 0 ? "text-[14px] py-s" : "text-m py-xs"} w-full no-underline flex justify-between items-center font-secondary font-medium`}
                          variant={ButtonVariant.TERTIARY}
                          size={ButtonSize.SMALL}
                          theme={ButtonTheme.BLACK}
                          href={getLink(column.viewAll, locale)}
                          onClick={() =>
                            browsePropertiesHeaderLinkClick(
                              { link_category: column?.title, link_name: column?.viewAll?.label || "" },
                              activeItem?.internalName || "",
                            )
                          }
                        >
                          {column?.viewAll?.label}
                        </Button>
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </section>
          {activeItem?.menuItemCta?.link && (
            <footer className="shrink-0 p-s text-center">
              <Button
                href={getLink(activeItem?.menuItemCta, locale)}
                customClass="inline-block"
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.BLACK}
                type="button"
                onClick={() => ctaClickTracking(activeItem?.menuItemCta?.label || "", "headerMobile", "", true)}
              >
                {activeItem?.menuItemCta?.label}
              </Button>
            </footer>
          )}
        </div>
      </FocusLock>
    </motion.div>
  );
};

const HeaderMobile: FC<HeaderProps> = ({
  logoDark,
  logoLight,
  menu,
  personalArea,
  shortlist,
  contactChannels,
  search,
  getInTouch,
  onLogin,
  settings,
}) => {
  const scroll = useScroll();
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const { isLoggedIn, isInventoryAvailable } = useContext(UserContext);
  const { fixedHeader, setVisibleHeader } = useContext(TemplateContext);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuItemRef = useRef<(HTMLDivElement | null)[]>([]);
  const [wasRedirected, setWasRedirected] = useState(false);
  const [isInvertyAvaliable, setIsInvertyAvaliable] = useState(false);
  const [isProjectPage, setIsProjectPage] = useState(false);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const headerMobileRef = useRef<HTMLDivElement>(null);
  const projectSlug = pathname?.split("/")?.find((segment, idx, arr) => arr[idx - 1] === "projects" || arr[idx - 1] === "communities");

  const {
    mobileMenuOpen,
    setMobileMenuOpen,
    mobilePanelID,
    setMobilePanelID,
    settingsVisible,
    setSettingsVisible,
    setLanguagesVisible,
    languagesVisible,
    transitionComplete,
    setMobileHeaderHeight,
  } = useContext(NavigationContext);

  //const { checkLoginError } = useContext(UserContext);

  // scroll assume un valore tra -1 e 1 a seconda della direzione
  // 0 significa che sono al top
  const scrollClasses: Record<string, string> = useMemo(() => {
    if (scroll === 0) {
      if (fixedHeader) {
        return pathname?.includes("terms-and-conditions") || pathname?.includes("privacy-policy") ? whiteMenu : transparentMenu;
      } else {
        return whiteMenu;
      }
    } else if (scroll > 0) {
      return {
        ...whiteMenu,
        container: `${pathname?.includes("terms-and-conditions") || pathname?.includes("privacy-policy") ? "top-[0]" : "top-[-100%]"}`,
      };
    } else {
      return { ...whiteMenu, containerBg: "shadow-strong bg-white" };
    }
  }, [scroll, fixedHeader, pathname]);

  // Update visibleHeader based on scroll direction (moved out of useMemo to avoid setState during render)
  useEffect(() => {
    if (scroll > 0) {
      setVisibleHeader(false);
    } else if (scroll < 0) {
      setVisibleHeader(true);
    }
  }, [scroll, setVisibleHeader]);

  const [scrollClass, setScrollClass] = useState("noscroll");

  useEffect(() => {
    setScrollClass(window.scrollY > 0 ? "scroll" : "noscroll");
  }, [scroll]);

  const callLink = useMemo(() => {
    return contactChannels?.find((item) => /phone/.test(`${item?.link?.icon}`));
  }, [contactChannels]);

  const gitInTouchLink = useMemo(() => {
    return contactChannels?.find((item) => item?.label === "Get in touch");
  }, [contactChannels]);

  const languageCallback = useCallback(() => {
    if (settingsVisible) {
      setLanguagesVisible(languagesVisible);
    }
    setLanguagesVisible(!languagesVisible);
    // ctaClickTracking(t("header.label.languages"), "HeaderMobile", "HeaderMobile");
    ctaClickTracking(DL_KEYS?.languages, "HeaderMobile", "HeaderMobile");
  }, [settingsVisible, languagesVisible, setLanguagesVisible]);

  const settingsCallback = useCallback(() => {
    if (languagesVisible) {
      setLanguagesVisible(settingsVisible);
    }
    setSettingsVisible(!settingsVisible);
    // ctaClickTracking(t("header.label.settings"), "HeaderMobile", "HeaderMobile");
    ctaClickTracking(DL_KEYS?.languages, "HeaderMobile", "HeaderMobile");
  }, [settingsVisible, languagesVisible, setLanguagesVisible, setSettingsVisible]);

  useEffect(() => {
    setMobileHeaderHeight(headerMobileRef?.current?.offsetHeight ?? 0);
  }, [headerMobileRef, setMobileHeaderHeight]);

  // Parsed once per menu, not on every header render (header re-renders on scroll)
  const menuExperiences = useMemo(() => menu?.items?.map((item) => parseCustomExperience(item)) ?? [], [menu?.items]);

  const { setGetInTouchModalOpen } = useContext(ModalsContext);
  const { utmParameters } = useContext(UTMParametersContext);
  useEffect(() => {
    if (typeof window !== "undefined") {
      const redirected = localStorage.getItem("redirected");
      const redirectedProject = localStorage.getItem("redirectedProject");
      const path = pathname.split("/").filter(Boolean).pop();
      if (path === redirectedProject) {
        setWasRedirected(redirected === "true");
      } else {
        // user navigated away from the redirected project page, clear the stored flags
        setWasRedirected(false);
        localStorage.removeItem("redirected");
        localStorage.removeItem("redirectedProject");
      }
    }
  }, [pathname]);

  useEffect(() => {
    const path = pathname.split("/").filter(Boolean).pop();
    const ProjectPage = pathname.split("/").filter(Boolean).at(-2);
    ProjectPage ? setIsProjectPage(ProjectPage === "projects") : setIsProjectPage(false);
    path === isInventoryAvailable.slug && setIsInvertyAvaliable(isInventoryAvailable.available);
  }, [isInventoryAvailable, pathname]);

  return (
    <>
      <div
        ref={headerMobileRef}
        id="header-mobile"
        className={`${scrollClasses?.container} ${scrollClasses?.containerBg} ${fixedHeader ? "fixed" : "fixed"} w-full transition-all ease-in-out duration-500 backdrop-blur-md z-20 md:hidden ${scrollClass}`}
      >
        <div className="flex items-center justify-between px-m py-xs">
          <div className="flex items-center">
            <HamburgerMenu
              ref={hamburgerRef}
              variant={scrollClasses?.hamburgerVariant}
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            />
            {search ? (
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NONE}
                type="button"
                aria-label="search-button"
                variant={ButtonVariant.PRIMARY}
                customClass="border-0 bg-transparent"
                onClick={() => {
                  setMobilePanelID("location");
                  searchInteractionTracking();
                  ctaClickTracking("Search", "HeaderMobile", "HeaderMobile");
                }}
              >
                <span className="flex items-center gap-xs">
                  {/* <Icon className={scrollClasses.iconStroke} name={search.link.icon ?? ""} size="20" title={search.label} /> */}
                  <Icon className={scrollClasses?.iconStroke} name="search" size="24" title={search?.label} />
                </span>
              </Button>
            ) : (
              <div className="w-l"></div>
            )}
          </div>
          <AnchorLink href={`/${locale}/`} aria-label={t("button.label.homepage")}>
            <Image
              fit="pad"
              className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 h-xl ${scrollClasses?.darkLogo}`}
              src={logoDark?.url ?? ""}
              width={170}
              height={48}
              alt={logoDark?.title}
              loading="eager"
            />
            <Image
              fit="pad"
              className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 h-xl ${scrollClasses?.lightLogo}`}
              src={logoLight?.url}
              width={170}
              height={48}
              alt={logoDark?.title}
              loading="eager"
            />
          </AnchorLink>
          <div className="flex items-center">
            {
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NONE}
                type="button"
                variant={ButtonVariant.PRIMARY}
                customClass="border-0 bg-transparent"
                // href={callLink.link.url}
                href={`tel:${getFallbackCampaignParameters(utmParameters?.utm_medium)?.virtual_number || "+97145205400"}`}
                poneNumberCta={() => {
                  contactChannelTracking(t("label.common.callus"), projectSlug || "");
                  ctaClickTracking(t("label.common.callus"), "ContactChannels", "ContactChannels");
                }}
              >
                <span className="flex items-center gap-xs">
                  <Icon className={scrollClasses?.iconStroke} name={callLink?.link?.icon || "phone"} size="24" title={callLink?.label} />
                </span>
              </Button>
            }
            {(getInTouch || gitInTouchLink) && (
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NONE}
                type="button"
                aria-label="get-in-touch-button"
                variant={ButtonVariant.PRIMARY}
                customClass="border-0 bg-transparent"
                onClick={() => {
                  setGetInTouchModalOpen(true);
                }}
              >
                <span className="flex items-center gap-xs">
                  {getInTouch?.link?.icon ? (
                    <Icon className={scrollClasses.iconStroke} name={getInTouch?.link?.icon} size="24" title={getInTouch?.label} />
                  ) : (
                    gitInTouchLink?.link?.icon && (
                      <Icon className={scrollClasses?.iconStroke} name={gitInTouchLink?.link?.icon} size="24" title={gitInTouchLink?.label} />
                    )
                  )}
                </span>
              </Button>
            )}
          </div>
        </div>
        {isProjectPage &&
          wasRedirected &&
          (isInvertyAvaliable ? (
            <></>
          ) : (
            <div className="flex justify-center items-center bg-gray-10">
              <span className="text-[12px] font-secondary font-medium p-[10px]">
                {t("title.label.propertyNotAvailable")}
                <Button
                  className={`text-[12px] font-secondary font-medium pb-[8px] pl-[8px] underline`}
                  variant={ButtonVariant.TERTIARY}
                  size={ButtonSize.SMALL}
                  theme={ButtonTheme.BLACK}
                  // href={props?.link?.id}
                  onClick={() => {
                    router.push(`/${locale}/search`);
                  }}
                >
                  {t("button.label.searchAnotherUnit")}
                </Button>
              </span>
            </div>
          ))}
      </div>
      {/* Main header mobile */}
      {/* Main menu mobile */}
      <motion.div
        id="mobile-panel-main"
        initial={variants.close}
        transition={transition}
        variants={variants}
        animate={mobileMenuOpen ? "open" : "close"}
        className={`fixed z-40 left-0 top-0 w-screen h-dvh bg-white `}
      >
        <FocusLock
          disabled={!mobileMenuOpen}
          className="h-full flex flex-col"
          autoFocus={true}
          group="main"
          returnFocus={() => {
            hamburgerRef?.current?.focus();
            return false;
          }}
        >
          <header className="flex justify-between items-center p-m">
            <p className="uppercase text-m overflow-hidden whitespace-nowrap text-ellipsis">{menu?.name}</p>
            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            >
              <Icon name="close" className="stroke-black" size="24" title={t("label.common.close")} />
            </button>
          </header>
          <div className="flex-1 overflow-auto py-xl px-m">
            <nav className="flex flex-col">
              {menu?.items?.map((item, index) => {
                const parsedExperiences = menuExperiences[index] || [];
                const buttonProps = { baseItem: item, itemIndex: index, menuItemRef };

                return (
                  <div key={`${item.id}-mobile-${index}`}>
                    {parsedExperiences.length > 0 ? (
                      <Experience
                        {...item}
                        key={`experience-button-mobile-${item.id}`}
                        id={item?.id as any}
                        component={MobileMenuButton as any}
                        experiences={parsedExperiences}
                        passthroughProps={buttonProps as any}
                      />
                    ) : (
                      <MobileMenuButton {...item} {...buttonProps} key={`experience-button-mobile-${item.id}`} />
                    )}
                  </div>
                );
              })}
            </nav>
            <hr className="my-xl border-white border-b border-b-gray-10" />
            <ContactChannels contactChannels={contactChannels} />
          </div>
          <footer className="grid grid-flow-col auto-cols-fr border-t border-t-gray-10">
            {personalArea && personalArea?.link && (
              <AnchorLink className="hidden py-m text-center" href={getLink(isLoggedIn ? personalArea : "", locale).toString()} span={!isLoggedIn}>
                <div className="flex flex-col items-center p-m">
                  <SoftLogin
                    icon={{ notLoggedIcon: personalArea?.link?.icon!, loggedIcon: "user-selected" }}
                    labelClass="font-secondary text-s mt-s"
                    label={personalArea?.label}
                  />
                </div>
              </AnchorLink>
            )}
            {shortlist && shortlist.link && (
              <AnchorLink className="py-m" href={getLink(shortlist, locale).toString()}>
                <div className="flex flex-col items-center p-m">
                  <Icon name={shortlist?.link?.icon || "heart-empty"} size="24" className="stroke-black" />
                  <span className="font-secondary text-s mt-s">{shortlist?.label}</span>
                </div>
              </AnchorLink>
            )}

            <Button
              variant={ButtonVariant.TERTIARY}
              size={ButtonSize.SMALL}
              theme={ButtonTheme.BLACK}
              type="button"
              className="py-m"
              disabled={!transitionComplete}
              onClick={settingsCallback}
            >
              <div className="flex flex-col items-center">
                <Icon className="stroke-black" name="settings" size="24" />
                <span className="font-secondary text-s mt-s">{t("header.label.settings")}</span>
              </div>
            </Button>
            {settings?.settings?.languages && settings?.settings?.languages?.length > 1 && (
              <Button
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.BLACK}
                type="button"
                className="py-m"
                disabled={!transitionComplete}
                onClick={languageCallback}
              >
                <div className="flex flex-col items-center">
                  <Icon className="stroke-black" name="flag" size="20" />
                  <span className="font-secondary text-s mt-s">{t("header.label.languages")}</span>
                </div>
              </Button>
            )}
          </footer>
        </FocusLock>
      </motion.div>
      {/* Main menu mobile */}
      {/* Menu mobile panels */}
      {menu?.items?.map((item, panelIndex) => {
        const parsedExperiences = menuExperiences[panelIndex] || [];
        const panelProps = { baseItem: item, highlightFirstCluster: menu?.highlightFirstCluster };

        return (
          <div key={`panel-${item.id}-${panelIndex}`}>
            {parsedExperiences.length > 0 ? (
              <Experience
                {...item}
                key={`experience-panel-mobile-${item.id}`}
                id={item?.id as any}
                component={MobileMenuPanel as any}
                experiences={parsedExperiences}
                passthroughProps={panelProps as any}
              />
            ) : (
              <MobileMenuPanel {...item} {...panelProps} key={`experience-panel-mobile-${item.id}`} />
            )}
          </div>
        );
      })}
      {/* Menu mobile panels */}
      {/* Location Search Mobile */}

      <Modal topPositioned className="md:hidden" open={mobilePanelID === "location"} onClose={() => setMobilePanelID("")}>
        <ModalContent removePadding className="w-full md:hidden h-dvh rounded-none rounded-t-none">
          <LocationMobile />
        </ModalContent>
      </Modal>
    </>
  );
};
export default HeaderMobile;
